import { ResponsiveContainer, AreaChart, Area } from "recharts";
import Hint from "./Hint";

export type CockpitKpi = {
  id: string;
  name: string;
  value: number;
  unit: string;
  delta_pct: number;
  direction: string;
  spark: number[];
  /** additiv seit dev-v122 - liegt in der API vor */
  value_state?: "measured" | "placeholder";
  value_ts?: string | null;
  value_source?: string | null;
  value_quality?: string | null;
  target_value?: number | null;
  attainment_pct?: number | null;
  target_reached?: boolean | null;
};

export function formatValue(v: number, unit: string): string {
  if (unit === "EUR") return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
  if (unit === "%") return v.toFixed(1) + " %";
  if (unit === "score") return (v >= 0 ? "+" : "") + Math.round(v);
  if (unit === "days") return Math.round(v) + " T";
  if (unit === "ratio") return "1 : " + v.toFixed(1);
  if (unit === "Index") return v.toFixed(0) + " (Index)";
  return `${v.toLocaleString("de-DE", { maximumFractionDigits: 2 })} ${unit}`.trim();
}

function attainmentClass(pct: number | null | undefined, reached: boolean | null | undefined): string {
  if (reached) return "gut";
  if (pct == null) return "unbekannt";
  if (pct >= 90) return "gut";
  if (pct >= 70) return "mittel";
  return "kritisch";
}

/**
 * KPI-Karte mit Zielerreichungs-Badge. Die Felder target_value/attainment_pct/
 * target_reached/value_state liefert /v1/cockpit/summary bereits mit - sie waren
 * bis dev-v120 im Frontend nur nicht sichtbar.
 */
export function KpiCard({ k }: { k: CockpitKpi }) {
  const measured = k.value_state !== "placeholder";
  const sparkData = (k.spark ?? []).map((v, i) => ({ i, v }));
  const positiveDelta =
    (k.direction === "up_is_favorable" && k.delta_pct > 0) ||
    (k.direction === "down_is_favorable" && k.delta_pct < 0);
  const cls = attainmentClass(k.attainment_pct, k.target_reached);
  const pct = k.attainment_pct;
  const bar = pct == null ? 0 : Math.max(0, Math.min(100, pct));

  return (
    <article className={`kpi-card kpi-card-v2 ${measured ? "measured" : "placeholder"}`}>
      <div className="kpi-top">
        <p className="kpi-label">{k.name}</p>
        {measured ? (
          <span className="kpi-state ok" title={
            [
              k.value_ts ? `Stand: ${new Date(k.value_ts).toLocaleDateString("de-DE")}` : null,
              k.value_source ? `Quelle: ${k.value_source}` : null,
              k.value_quality ? `Qualität: ${k.value_quality}` : null,
            ].filter(Boolean).join(" · ") || "Gemessener Wert"
          }>gemessen</span>
        ) : (
          <span className="kpi-state warn">
            <Hint term="platzhalter">Platzhalter</Hint>
          </span>
        )}
      </div>

      <div className={`kpi-value ${measured ? "" : "is-placeholder"}`}>{formatValue(k.value, k.unit)}</div>

      {measured ? (
        k.target_value != null ? (
          <div className={`kpi-target att-${cls}`}>
            <div className="kpi-target-row">
              <span>
                Ziel {formatValue(k.target_value, k.unit)}
                <Hint term="zielerreichung" />
              </span>
              <strong>
                {pct != null ? `${pct.toFixed(1)} %` : "-"}
                {k.target_reached ? " ✓" : ""}
              </strong>
            </div>
            <div className="kpi-target-bar" role="img" aria-label={`Zielerreichung ${pct != null ? pct.toFixed(1) : "unbekannt"} Prozent`}>
              <span style={{ width: `${bar}%` }} />
            </div>
            <p className="kpi-target-note">
              {k.target_reached
                ? "Ziel erreicht."
                : pct != null
                  ? `Noch ${(100 - pct).toFixed(1)} Prozentpunkte bis zum Ziel.`
                  : "Kein Zielwert hinterlegt."}
            </p>
          </div>
        ) : (
          <div className="kpi-target att-unbekannt">
            <p className="kpi-target-note">Kein Zielwert im Metric Contract hinterlegt.</p>
          </div>
        )
      ) : (
        <div className="kpi-target att-unbekannt">
          <p className="kpi-target-note">
            Noch kein Messwert - diese Zahl ist ein Fuellwert und darf nicht interpretiert werden.
          </p>
        </div>
      )}

      {measured && k.delta_pct !== 0 && (
        <span className={`kpi-delta ${positiveDelta ? "up" : "down"}`}>
          {positiveDelta ? "▲" : "▼"} {Math.abs(k.delta_pct).toFixed(1)} % ggue. Vorwert
        </span>
      )}
      {measured && k.delta_pct === 0 && (
        <span className="kpi-delta flat">■ unverändert (erst ein Messpunkt)</span>
      )}

      {sparkData.length > 1 && (
        <div style={{ height: 36, marginTop: 10 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData}>
              <defs>
                <linearGradient id={`g-${k.id}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={positiveDelta ? "#35c88a" : "#e75f6b"} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={positiveDelta ? "#35c88a" : "#e75f6b"} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={positiveDelta ? "#35c88a" : "#e75f6b"} strokeWidth={1.5} fill={`url(#g-${k.id})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </article>
  );
}

export default KpiCard;
